import { Link } from "react-router-dom";
import { Play, ArrowRight, GraduationCap, Briefcase, Sparkles, FlaskConical } from "lucide-react";
import SignalField from "@/components/SignalField";
import SectionHeading from "@/components/SectionHeading";
import CTA from "@/components/CTA";
import { useI18n } from "@/i18n/I18nProvider";

export default function HomePage() {
  const { t } = useI18n();
  const spotlight = t("home.spotlight.cards");
  const spotlightIcons = [Sparkles, GraduationCap, FlaskConical];
  const spotlightTos = ["/about", "/for-business", "/about"];

  return (
    <>
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-brand-dark"
      >
        <SignalField className="absolute inset-0 w-full h-full" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-brand-dark/60 pointer-events-none" />

        <div className="container-x relative z-10 pt-24 pb-28 md:pt-36 md:pb-40">
          <div className="max-w-3xl animate-fade-up">
            <p className="eyebrow-light mb-6" data-testid="hero-eyebrow">
              {t("home.hero.eyebrow")}
            </p>
            <h1
              data-testid="hero-headline"
              className="text-brand-off text-[42px] sm:text-6xl md:text-[76px] leading-[1.02] tracking-tight font-light"
            >
              {t("home.hero.title1")}
              <br />
              <span className="text-white">{t("home.hero.title2")}</span>
            </h1>
            <p
              data-testid="hero-subheadline"
              className="mt-7 text-white/75 text-[16.5px] sm:text-lg leading-relaxed max-w-2xl"
            >
              {t("home.hero.subtitle")}
            </p>
            <p className="mt-3 text-white/55 text-[14.5px] max-w-2xl">
              {t("home.hero.tagline")}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <CTA
                href="#intro-video"
                variant="solid-light"
                testid="hero-cta-watch"
              >
                <Play className="w-3.5 h-3.5" />
                {t("home.hero.ctaWatch")}
              </CTA>
              <CTA
                to="/about"
                variant="light-outline"
                testid="hero-cta-explore"
              >
                {t("home.hero.ctaExplore")}
              </CTA>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 right-0 z-10 container-x flex items-center gap-2 text-white/40 text-[12px] tracking-[0.18em] uppercase">
          <span className="inline-block w-6 h-px bg-white/30" />
          Signal, not noise
        </div>
      </section>

      {/* TWO PATHS */}
      <section data-testid="paths-section" className="bg-brand-off py-20 md:py-32">
        <div className="container-x">
          <SectionHeading
            eyebrow={t("home.paths.eyebrow")}
            title={t("home.paths.title")}
            description={t("home.paths.description")}
            className="mb-14"
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
            <PathCard
              tag={t("home.paths.students.tag")}
              title={t("home.paths.students.title")}
              desc={t("home.paths.students.desc")}
              bullets={t("home.paths.students.bullets")}
              cta={t("home.paths.students.cta")}
              to="/for-students"
              icon={GraduationCap}
              testid="path-students"
            />
            <PathCard
              tag={t("home.paths.business.tag")}
              title={t("home.paths.business.title")}
              desc={t("home.paths.business.desc")}
              bullets={t("home.paths.business.bullets")}
              cta={t("home.paths.business.cta")}
              to="/for-business"
              icon={Briefcase}
              testid="path-business"
              accent
            />
          </div>
        </div>
      </section>

      {/* VIDEO + INTRO */}
      <section
        id="intro-video"
        data-testid="video-section"
        className="bg-brand-paper py-20 md:py-28 border-t border-brand-line"
      >
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <button
              type="button"
              data-testid="video-placeholder"
              className="group relative w-full aspect-video rounded-2xl overflow-hidden bg-brand-darker soft-shadow soft-shadow-hover hover-lift border border-black/5"
            >
              <SignalField className="absolute inset-0 w-full h-full opacity-90" />
              <div className="absolute inset-0 bg-brand-charcoal/30" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                <span className="flex items-center justify-center w-16 h-16 rounded-full bg-white/95 text-brand-charcoal shadow-lg transition-transform group-hover:scale-105">
                  <Play className="w-6 h-6 ml-1" />
                </span>
                <span className="text-white/80 text-[13px] uppercase tracking-[0.18em] text-center px-6">
                  {t("home.intro.videoComing")}
                </span>
              </div>
            </button>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="eyebrow mb-4">{t("home.intro.eyebrow")}</p>
            <p className="text-[17px] leading-[1.7] text-brand-charcoal/90">
              {t("home.intro.paragraph1")}
            </p>
            <p className="mt-4 text-[16px] leading-[1.7] text-brand-muted">
              {t("home.intro.paragraph2")}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <CTA to="/about" variant="outline" testid="intro-cta-about" arrow>
                {t("home.intro.ctaAbout")}
              </CTA>
              <CTA to="/audit" variant="ghost" testid="intro-cta-audit" arrow>
                {t("home.intro.ctaAudit")}
              </CTA>
            </div>
          </div>
        </div>
      </section>

      {/* SPOTLIGHT */}
      <section
        data-testid="spotlight-section"
        className="bg-brand-off py-20 md:py-28"
      >
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <SectionHeading
              eyebrow={t("home.spotlight.eyebrow")}
              title={t("home.spotlight.title")}
              description={t("home.spotlight.description")}
            />
            <Link
              to="/about"
              data-testid="spotlight-see-all"
              className="self-start md:self-end text-[14px] text-brand-muted hover:text-brand-charcoal transition-colors inline-flex items-center gap-1"
            >
              {t("common.seeAll")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {Array.isArray(spotlight) &&
              spotlight.map((item, idx) => {
                const Icon = spotlightIcons[idx] || Sparkles;
                return (
                  <article
                    key={idx}
                    data-testid={`spotlight-${idx + 1}`}
                    className="group relative flex flex-col p-7 md:p-8 rounded-2xl bg-white border border-black/[0.06] soft-shadow soft-shadow-hover hover-lift"
                  >
                    <div className="flex items-center gap-2 mb-6">
                      <Icon className="w-3.5 h-3.5 text-brand-blue" />
                      <span className="eyebrow">{item.tag}</span>
                    </div>
                    <h3 className="text-[22px] leading-[1.25] font-medium text-brand-charcoal tracking-tight">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-[14.5px] leading-relaxed text-brand-muted">
                      {item.desc}
                    </p>
                    <div className="mt-7 pt-5 border-t border-brand-line/80">
                      <Link
                        to={spotlightTos[idx] || "/about"}
                        className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-charcoal group-hover:text-brand-blue transition-colors"
                      >
                        {item.cta}
                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </article>
                );
              })}
          </div>
        </div>
      </section>

      {/* CLOSING — audit + student split */}
      <section
        data-testid="closing-section"
        className="bg-brand-charcoal text-brand-off py-20 md:py-28"
      >
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow-light mb-5">{t("home.closing.eyebrow")}</p>
            <h2 className="text-3xl sm:text-4xl md:text-[44px] font-light tracking-tight leading-[1.1] text-brand-off max-w-2xl">
              {t("home.closing.title")}
            </h2>
            <p className="mt-5 text-white/65 text-[16px] max-w-xl leading-relaxed">
              {t("home.closing.description")}
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
            <CTA to="/audit" variant="solid-light" testid="closing-cta-audit">
              {t("home.closing.cta")}
            </CTA>
            <CTA to="/for-students" variant="light-outline" testid="closing-cta-students" arrow>
              {t("home.closing.ctaStudent")}
            </CTA>
          </div>
        </div>
      </section>
    </>
  );
}

function PathCard({ tag, title, desc, bullets, cta, to, icon: Icon, testid, accent }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group relative flex flex-col p-8 md:p-10 rounded-2xl border overflow-hidden hover-lift soft-shadow soft-shadow-hover ${accent ? "bg-brand-charcoal text-brand-off border-white/10" : "bg-white text-brand-charcoal border-black/[0.06]"}`}
    >
      <div className="flex items-center justify-between mb-7">
        <span className={`inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase ${accent ? "text-white/65" : "text-brand-muted"}`}>
          <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
          {tag}
        </span>
        <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${accent ? "text-white/80" : "text-brand-charcoal/70"}`} />
      </div>
      <h3 className={`text-[26px] sm:text-[30px] md:text-[34px] font-light tracking-tight leading-[1.1] ${accent ? "text-brand-off" : "text-brand-charcoal"}`}>
        {title}
      </h3>
      <p className={`mt-5 text-[15.5px] leading-relaxed max-w-md ${accent ? "text-white/70" : "text-brand-muted"}`}>
        {desc}
      </p>
      {Array.isArray(bullets) && (
        <ul className="mt-7 space-y-2.5 text-[14.5px]">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className={`mt-2 inline-block w-1.5 h-1.5 rounded-full ${accent ? "bg-brand-cyan" : "bg-brand-blue"}`} />
              <span className={accent ? "text-white/85" : "text-brand-charcoal/90"}>{b}</span>
            </li>
          ))}
        </ul>
      )}
      <div className={`mt-10 inline-flex items-center gap-2 text-[14px] font-medium ${accent ? "text-brand-cyan" : "text-brand-blue"}`}>
        {cta} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  );
}
